<template>
  <v-app>
    <app-top-bar :class="hasBackgroundImage ? '' : 'bg-background'" :path-return="pathPage">{{
      $t("clientCPF.pageTitle")
    }}</app-top-bar>
    <v-main
      :style="mainStyle"
      :class="hasBackgroundImage ? '' : 'bg-background'"
      class="text-on-surface-variant"
    >
      <v-container v-if="!showComplete">
        <h1 class="page-title mb-6">{{ $t("clientCPF.mainTitle") }}</h1>

        <v-text-field
          v-model="cpf"
          v-mask="['###.###.###-##']"
          class="mb-1 text-on-surface-variant"
          variant="underlined"
          :label="$t('clientCPF.cpfLabel')"
          type="tel"
          color="primary"
          background-color="surface"
        />

        <v-text-field
          v-model="birthDate"
          v-mask="['##/##/####']"
          class="mb-1 text-on-surface-variant"
          variant="underlined"
          :label="$t('clientCPF.birthDateLabel')"
          :rules="birthDateInputRules"
          type="tel"
          color="primary"
          background-color="surface"
        />
      </v-container>
      <v-container v-if="showComplete">
        <phase-completed
          :title="$t('clientCPF.completedTitle')"
          :description="$t('clientCPF.completedDescription')"
        />
      </v-container>
    </v-main>

    <app-footer>
      <continue-footer-button
        v-if="!showComplete"
        :disabled="!isCpfInputValid || !isBirthDateInputValid"
        :loading="isAdvancing"
        @click="onConfirm"
      />
      <continue-footer-button v-if="showComplete" :loading="isAdvancing" @click="advanceFlow" />
    </app-footer>
  </v-app>
</template>

<script setup lang="ts">
  import { usePageBackground } from "@/composables/usePageBackground";
  import { useStepFlow } from "@/composables/useStepFlow";
  import { useUtils } from "@/composables/useUtils";
  import { computed, ref } from "vue";
  import { useI18n } from "vue-i18n";
  import { useRoute } from "vue-router";
  import { mask as vMask } from "vue-the-mask";

  import { useApiStore } from "@/stores/api";
  import { useAppStore } from "@/stores/app";
  import { showToast } from "@/utils/toast";

  const { t } = useI18n();

  const route = useRoute();
  const api = useApiStore();
  const store = useAppStore();
  const { mainStyle, hasBackgroundImage } = usePageBackground();
  const util = useUtils();
  const pathPage = "/client-cpf";

  const {
    isAdvancing,
    showComplete,
    advance: advanceFlow,
  } = useStepFlow(pathPage, () => isComplete.value);

  const isComplete = computed<boolean>(() => api.isItemComplete("cpf"));

  const MIN_AGE = 18;

  const cpf = ref<string | null>(api.client.cpf ?? null);
  const birthDate = ref<string | null>(formatBirthDateForDisplay(api.client?.birthDate ?? null));

  const isCpfInputValid = computed<boolean>(() => cpf.value && cpf.value.length === 14);

  function parseBirthDate(value: string | null): Date | null {
    if (!value || value.length !== 10) return null;

    const [day, month, year] = value.split("/").map(Number);
    if (!day || !month || !year) return null;

    const date = new Date(year, month - 1, day);
    const isRealDate =
      date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;

    return isRealDate ? date : null;
  }

  function formatBirthDateForDisplay(isoDate: string | null): string | null {
    if (!isoDate) return null;

    const [year, month, day] = isoDate.split("-");
    if (!year || !month || !day) return null;

    return `${day}/${month}/${year}`;
  }

  function toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  function hasMinAge(date: Date, minAge: number): boolean {
    const today = new Date();
    const limitDate = new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate());

    return date <= limitDate;
  }

  const birthDateInputRules = [
    (v: string | null) => !!v || t("clientCPF.birthDateRequired"),
    (v: string | null) => !v || !!parseBirthDate(v) || t("clientCPF.birthDateInvalid"),
    (v: string | null) => {
      const parsed = parseBirthDate(v);
      return !parsed || hasMinAge(parsed, MIN_AGE) || t("clientCPF.birthDateMinAge");
    },
  ];

  const parsedBirthDate = computed<Date | null>(() => parseBirthDate(birthDate.value));

  const isBirthDateInputValid = computed<boolean>(
    () => !!parsedBirthDate.value && hasMinAge(parsedBirthDate.value, MIN_AGE),
  );

  async function onConfirm() {
    const formmatedCpf = cpf.value.replace(/\D/g, "");
    const birthDateIso = toIsoDate(parsedBirthDate.value as Date);
    try {
      await advanceFlow(async () => {
        await api.setCPF(cpf.value, birthDateIso);
        if (api.bikeDANFECode) {
          await api.verifyUserCpf(formmatedCpf, api.bikeDANFECode);
        }
        if (route.params.dev === "true") {
          showToast.info({
            title: t("clientCPF.danfeTitle"),
            description: api.bikeDANFECode,
          });
        }
        if (api.client) {
          api.client.cpf = cpf.value.replace(/\D/g, "");
          api.client.birthDate = birthDateIso;
        }
      });
    } catch (error) {
      console.error(error);
      util.noticeError(error);
    }
  }
</script>
